import { memo, useMemo } from "react";
import Chart from "react-apexcharts";

import "./OperatorPerformanceChart.scss";

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const safePercentage = (value) =>
  Math.min(100, Math.max(0, safeNumber(value)));

const cleanName = (name = "") =>
  String(name).replace(/\s+/g, " ").trim() || "Unknown";

const percentageFormatter = (value) =>
  `${Math.round(safeNumber(value))}%`;

const tooltipPercentageFormatter = (value) =>
  `${safeNumber(value).toFixed(2)}%`;

function OperatorPerformanceChart({
  operators = [],
  chartKey = "default",
  loading = false,
}) {
  const chartData = useMemo(() => {
    const operatorList = Array.isArray(operators)
      ? operators.filter(Boolean).slice(0, 10)
      : [];

    const categories = [];
    const durationData = [];
    const qualityData = [];

    operatorList.forEach((operator) => {
      categories.push(
        cleanName(operator?.operatorUserName),
      );

      durationData.push(
        safePercentage(operator?.durationUtilization),
      );

      qualityData.push(
        safePercentage(operator?.qualityPercentage),
      );
    });

    return {
      categories,
      durationData,
      qualityData,
    };
  }, [operators]);

  const series = useMemo(
    () => [
      {
        name: "Duration Utilization",
        type: "column",
        data: chartData.durationData,
      },
      {
        name: "Quality",
        type: "line",
        data: chartData.qualityData,
      },
    ],
    [chartData.durationData, chartData.qualityData],
  );

  const animationKey = useMemo(
    () =>
      [
        chartKey,
        chartData.categories.join("-"),
        chartData.durationData.join("-"),
        chartData.qualityData.join("-"),
      ].join("_"),
    [
      chartKey,
      chartData.categories,
      chartData.durationData,
      chartData.qualityData,
    ],
  );

  const options = useMemo(
    () => ({
      chart: {
        id: `operator-performance-${chartKey}`,
        type: "line",
        stacked: false,

        toolbar: {
          show: false,
        },

        zoom: {
          enabled: false,
        },

        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 1100,

          animateGradually: {
            enabled: true,
            delay: 130,
          },

          dynamicAnimation: {
            enabled: true,
            speed: 700,
          },
        },

        redrawOnParentResize: false,
        redrawOnWindowResize: false,
      },

      colors: ["#7c3aed", "#10b981"],

      stroke: {
        width: [0, 3],
        curve: "smooth",
        lineCap: "round",
      },

      markers: {
        size: [0, 4],
        strokeWidth: 3,
        strokeColors: "#ffffff",
        hover: {
          size: 7,
        },
      },

      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "48%",
          borderRadius: 6,
          borderRadiusApplication: "end",
          dataLabels: {
            position: "top",
          },
        },
      },

      fill: {
        type: ["gradient", "solid"],
        opacity: [0.95, 1],

        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.2,
          gradientToColors: ["#5b21b6"],
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 0.82,
          stops: [0, 100],
        },
      },

      dataLabels: {
        enabled: false,
      },

      grid: {
        show: true,
        borderColor: "#eef2f7",
        strokeDashArray: 4,

        padding: {
          top: 8,
          right: 10,
          bottom: 0,
          left: 5,
        },
      },

      xaxis: {
        type: "category",
        categories: chartData.categories,

        axisBorder: {
          show: false,
        },

        axisTicks: {
          show: false,
        },

        labels: {
          rotate: -35,
          rotateAlways: chartData.categories.length > 5,
          trim: true,
          hideOverlappingLabels: true,
          maxHeight: 90,

          style: {
            fontSize: "10px",
            fontWeight: 600,
            colors: "#64748b",
          },

          formatter: (value) => {
            const text = cleanName(value);

            return text.length > 16
              ? `${text.slice(0, 16)}...`
              : text;
          },
        },
      },

      yaxis: [
        {
          seriesName: "Duration Utilization",
          min: 0,
          max: 100,
          tickAmount: 5,

          labels: {
            formatter: percentageFormatter,

            style: {
              fontSize: "11px",
              colors: "#64748b",
            },
          },

          title: {
            text: "Duration %",

            style: {
              color: "#7c3aed",
              fontSize: "10px",
              fontWeight: 700,
            },
          },
        },
        {
          seriesName: "Quality",
          opposite: true,
          min: 0,
          max: 100,
          tickAmount: 5,

          labels: {
            formatter: percentageFormatter,

            style: {
              fontSize: "10px",
              colors: "#64748b",
            },
          },

          title: {
            text: "Quality %",

            style: {
              color: "#059669",
              fontSize: "10px",
              fontWeight: 700,
            },
          },
        },
      ],

      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        fontSize: "10px",
        fontWeight: 600,

        markers: {
          width: 9,
          height: 9,
          radius: 10,
        },

        itemMargin: {
          horizontal: 10,
          vertical: 4,
        },
      },

      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,

        y: {
          formatter: tooltipPercentageFormatter,
        },
      },

      states: {
        normal: {
          filter: {
            type: "none",
          },
        },

        hover: {
          filter: {
            type: "lighten",
            value: 0.08,
          },
        },

        active: {
          allowMultipleDataPointsSelection: false,

          filter: {
            type: "darken",
            value: 0.06,
          },
        },
      },

      noData: {
        text: "No duration performance data",
        align: "center",
        verticalAlign: "middle",

        style: {
          color: "#94a3b8",
          fontSize: "12px",
        },
      },
    }),
    [chartData.categories, chartKey],
  );

  if (loading) {
    return (
      <div className="operator-performance-chart operator-performance-chart--loading">
        <div className="operator-performance-skeleton">
          <div className="operator-performance-skeleton__legend">
            <span />
            <span />
          </div>

          <div className="operator-performance-skeleton__chart">
            {Array.from({ length: 8 }).map(
              (_, index) => (
                <div
                  key={`performance-skeleton-${index}`}
                  className="operator-performance-skeleton__column"
                >
                  <span
                    className="operator-performance-skeleton__bar"
                    style={{
                      height: `${
                        30 + ((index * 13) % 62)
                      }%`,
                      animationDelay: `${index * 80}ms`,
                    }}
                  />

                  <span className="operator-performance-skeleton__label" />
                </div>
              ),
            )}

            <svg
              className="operator-performance-skeleton__line"
              viewBox="0 0 800 220"
              preserveAspectRatio="none"
            >
              <path
                d="M20 155 C90 90, 145 125, 210 80 S330 130, 400 75 S530 110, 590 60 S710 120, 780 50"
                fill="none"
              />
            </svg>
          </div>

          <div className="operator-performance-skeleton__loading-text">
            <span className="operator-performance-spinner" />
            <span>Loading operator performance...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!chartData.categories.length) {
    return (
      <div className="operator-performance-chart operator-performance-chart--empty">
        <span className="operator-performance-chart__empty-icon">
          %
        </span>

        <span>No duration performance data</span>
      </div>
    );
  }

  return (
    <div
      key={animationKey}
      className="operator-performance-chart operator-performance-chart--visible"
    >
      <Chart
        options={options}
        series={series}
        type="line"
        width="100%"
        height={390}
      />
    </div>
  );
}

export default memo(OperatorPerformanceChart);